import type {
  ProTableDictOption,
  ProTableRenderContext,
  ProTableRenderPlugin,
  ProTableToolbarPlugin,
} from '@/components/FaProTable/types'
import { FaAvatar, FaButton, FaCode, FaDropdown, FaIcon, FaProgress, FaSwitch, FaTag, FaTooltip } from '@fantastic-admin/components'
import dayjs from 'dayjs'
import { ElImage } from 'element-plus'
import AppStatusTag from '@/components/AppDisplay/StatusTag.vue'

const renderPlugins = shallowRef<ProTableRenderPlugin[]>([])
const toolbarPlugins = shallowRef<ProTableToolbarPlugin[]>([])
let builtinsReady = false

function sortByOrder<T extends { order?: number }>(items: T[]) {
  return [...items].sort((a, b) => (a.order ?? 100) - (b.order ?? 100))
}

function resolveDictItem(
  dict: ProTableDictOption[] | Record<string | number, string> | undefined,
  rawValue: unknown,
): ProTableDictOption | undefined {
  if (!dict) {
    return undefined
  }

  if (Array.isArray(dict)) {
    return dict.find(item => item.value === rawValue || String(item.value) === String(rawValue))
  }

  const label = dict[String(rawValue ?? '')]
  return label == null
    ? undefined
    : {
        label,
        value: rawValue as ProTableDictOption['value'],
      }
}

function normalizeDictValues(value: unknown, props: Record<string, any>) {
  if (Array.isArray(value)) {
    return value
  }

  if (typeof value === 'string' && props.multiple && props.separator) {
    return value.split(props.separator).map(item => item.trim()).filter(Boolean)
  }

  return [value]
}

function formatMoney(value: unknown, props: Record<string, any>) {
  const amount = Number(value ?? 0)
  if (!Number.isFinite(amount)) {
    return props.emptyText ?? '-'
  }

  return new Intl.NumberFormat(props.locale ?? 'zh-CN', {
    style: 'currency',
    currency: props.currency ?? 'CNY',
    minimumFractionDigits: props.minimumFractionDigits ?? 2,
    maximumFractionDigits: props.maximumFractionDigits ?? 2,
  }).format(amount)
}

function shouldShowBadge(value: unknown) {
  switch (typeof value) {
    case 'string':
      return value.length > 0
    case 'number':
      return value > 0
    case 'boolean':
      return value
    default:
      return value !== undefined && value !== null
  }
}

function stringifyCode(value: unknown, props: Record<string, any>) {
  if (typeof value === 'string') {
    return value
  }

  try {
    return JSON.stringify(value, null, props.indent ?? 2)
  }
  catch {
    return String(value ?? '')
  }
}

function addRenderPlugin(plugin: ProTableRenderPlugin) {
  renderPlugins.value = [
    ...renderPlugins.value.filter(item => item.name !== plugin.name),
    plugin,
  ]
}

function removeRenderPlugin(name: string) {
  renderPlugins.value = renderPlugins.value.filter(item => item.name !== name)
}

function getRenderPlugins() {
  return renderPlugins.value
}

function getRenderPluginByName(name: string) {
  return renderPlugins.value.find(item => item.name === name)
}

function addToolbarPlugin(plugin: ProTableToolbarPlugin) {
  toolbarPlugins.value = sortByOrder([
    ...toolbarPlugins.value.filter(item => item.name !== plugin.name),
    plugin,
  ])
}

function removeToolbarPlugin(name: string) {
  toolbarPlugins.value = toolbarPlugins.value.filter(item => item.name !== name)
}

function getToolbarPlugin(name: string) {
  return toolbarPlugins.value.find(item => item.name === name)
}

function getToolbarPlugins() {
  return toolbarPlugins.value
}

function hideToolbarPlugin(name: string) {
  toolbarPlugins.value = toolbarPlugins.value.map((item) => {
    if (item.name === name) {
      return { ...item, show: false }
    }
    return item
  })
}

function showToolbarPlugin(name: string) {
  toolbarPlugins.value = toolbarPlugins.value.map((item) => {
    if (item.name === name) {
      return { ...item, show: true }
    }
    return item
  })
}

function ensureBuiltins() {
  if (builtinsReady) {
    return
  }
  builtinsReady = true

  addRenderPlugin({
    name: 'tag',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const { value } = context
      return h(FaTag, {
        variant: props.variant ?? 'secondary',
        icon: props.icon,
        class: props.class,
      }, () => String(value ?? ''))
    },
  })

  addRenderPlugin({
    name: 'link',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const { value } = context
      if (!value) {
        return '-'
      }
      const { text, class: className, ...linkProps } = props
      return h('a', {
        ...linkProps,
        href: String(value),
        target: '_blank',
        rel: 'noopener noreferrer',
        class: ['text-primary underline-offset-4 hover:underline', className],
      }, text ?? String(value))
    },
  })

  addRenderPlugin({
    name: 'image',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const { value } = context
      if (!value) {
        return '-'
      }
      return h(ElImage, {
        src: String(value),
        previewSrcList: [String(value)],
        previewTeleported: true,
        fit: 'cover',
        style: {
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          ...(props.style ?? {}),
        },
        ...props,
      })
    },
  })

  addRenderPlugin({
    name: 'image-group',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const images = Array.isArray(context.value) ? context.value : []
      if (!images.length) {
        return props.emptyText ?? '-'
      }

      const visibleCount = props.limit ?? 3
      const visibleImages = images.slice(0, visibleCount)
      const remainCount = images.length - visibleImages.length
      const previewList = images.map((item: any) => String(item?.url ?? item?.src ?? item))
      const thumbClass = props.thumbClass ?? 'size-10 rounded-lg border object-cover bg-muted'

      return h('div', { class: props.wrapperClass ?? 'flex items-center gap-2' }, [
        ...visibleImages.map((item: any, index: number) => {
          const src = String(item?.url ?? item?.src ?? item)
          const previewIndex = images.findIndex((candidate: any) => String(candidate?.url ?? candidate?.src ?? candidate) === src)
          return h(ElImage, {
            key: `${src}_${index}`,
            src,
            previewSrcList: previewList,
            initialIndex: previewIndex < 0 ? index : previewIndex,
            previewTeleported: true,
            fit: 'cover',
            class: thumbClass,
          })
        }),
        remainCount > 0
          ? h('div', {
              class: props.moreClass ?? 'size-10 flex items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground',
            }, `+${remainCount}`)
          : null,
      ])
    },
  })

  addRenderPlugin({
    name: 'datetime',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const { value } = context
      if (!value) {
        return props.emptyText ?? '-'
      }
      return dayjs(String(value)).format(props.format ?? 'YYYY-MM-DD HH:mm:ss')
    },
  })

  addRenderPlugin({
    name: 'code',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const code = stringifyCode(context.value, props)
      if (!code) {
        return props.emptyText ?? '-'
      }

      return h(FaCode as any, {
        code,
        class: props.class ?? 'max-h-44 max-w-96 overflow-auto text-xs',
      })
    },
  })

  addRenderPlugin({
    name: 'json',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const code = stringifyCode(context.value, props)
      if (!code) {
        return props.emptyText ?? '-'
      }

      if (props.mode === 'code') {
        return h(FaCode as any, {
          code,
          class: props.class ?? 'max-h-44 max-w-96 overflow-auto text-xs',
        })
      }

      const maxWidth = props.maxWidth ?? '240px'
      return h(FaTooltip as any, {
        delay: props.delay,
      }, {
        default: () => h('pre', {
          class: props.class ?? 'm-0 max-w-full overflow-hidden whitespace-pre-wrap break-all text-xs text-muted-foreground',
          style: { maxWidth },
        }, code),
        content: () => h(FaCode as any, {
          code,
          class: props.tooltipClass ?? 'max-h-72 max-w-120 overflow-auto text-xs',
        }),
      })
    },
  })

  addRenderPlugin({
    name: 'bool-tag',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const { value } = context
      const truthy = Boolean(value)
      return h(AppStatusTag, {
        label: truthy ? (props.trueText ?? '是') : (props.falseText ?? '否'),
        tone: truthy ? (props.trueType ?? 'success') : (props.falseType ?? 'default'),
        size: props.size,
        appearance: props.appearance,
      })
    },
  })

  addRenderPlugin({
    name: 'dict',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const values = normalizeDictValues(context.value, props)
      const items = values
        .map(value => resolveDictItem(props.dict, value))
        .filter(Boolean) as ProTableDictOption[]

      if (!items.length) {
        return props.emptyText ?? '-'
      }

      const renderText = () => items.map(item => item.label).join(props.joiner ?? ' / ')
      if (!props.tag) {
        return renderText()
      }

      return h('div', { class: 'flex flex-wrap items-center gap-1' }, items.map(item => h(FaTag, {
        variant: props.variant ?? 'secondary',
        class: item.className,
      }, () => [
        item.icon ? h(FaIcon as any, { name: item.icon, class: 'mr-1 size-3' }) : null,
        item.label,
      ])))
    },
  })

  addRenderPlugin({
    name: 'status-tag',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const item = resolveDictItem(props.dict ?? props.statuses, context.value)
      if (!item) {
        return props.emptyText ?? '-'
      }

      return h(AppStatusTag, {
        label: item.label,
        tone: item.type ?? props.type ?? 'default',
        icon: item.icon,
        class: item.className,
        size: props.size,
        appearance: props.appearance,
      })
    },
  })

  addRenderPlugin({
    name: 'avatar',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const src = String(context.value ?? props.src ?? '')
      const fallback = props.fallback
        ?? props.text
        ?? (props.textField ? context.row?.[props.textField] : '')
        ?? ''
      const text = props.text ?? (props.textField ? context.row?.[props.textField] : '')

      return h('div', { class: props.wrapperClass ?? 'flex items-center gap-2 min-w-0' }, [
        h(FaAvatar as any, {
          src,
          fallback: String(fallback || '').slice(0, props.fallbackLength ?? 2).toUpperCase(),
          class: props.avatarClass ?? 'size-8 rounded-full shrink-0',
        }),
        props.showText === false
          ? null
          : h('span', { class: props.textClass ?? 'truncate' }, String(text ?? '')),
      ])
    },
  })

  addRenderPlugin({
    name: 'badge',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const badgeValue = props.value ?? context.value
      const iconName = props.icon ?? 'i-ri:notification-3-line'
      const visible = shouldShowBadge(badgeValue)
      const isDot = badgeValue === true

      return h('div', {
        class: props.class ?? 'relative inline-flex min-h-7 min-w-7 items-center justify-center overflow-visible px-1 py-1',
      }, [
        props.text
          ? h('span', { class: props.textClass ?? 'truncate max-w-28 text-sm' }, String(props.text))
          : h(FaIcon as any, { name: iconName, class: props.iconClass ?? 'size-4 text-muted-foreground' }),
        visible
          ? h('span', {
              class: [
                isDot
                  ? 'absolute right-0 top-0 size-2 rounded-full'
                  : 'absolute right-0 top-0 min-w-4 rounded-full px-1 text-center text-[10px] leading-4 font-medium text-white',
                props.variant === 'secondary'
                  ? 'bg-secondary text-secondary-foreground'
                  : props.variant === 'destructive'
                    ? 'bg-destructive'
                    : 'bg-primary',
                props.badgeClass ?? '',
              ].join(' '),
            }, isDot ? '' : String(badgeValue))
          : null,
      ])
    },
  })

  addRenderPlugin({
    name: 'files',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const files = Array.isArray(context.value) ? context.value : []
      if (!files.length) {
        return props.emptyText ?? '-'
      }

      const visibleCount = props.limit ?? 2
      const visibleFiles = files.slice(0, visibleCount)
      const remainCount = files.length - visibleFiles.length

      return h('div', { class: props.wrapperClass ?? 'flex flex-wrap items-center gap-2' }, [
        ...visibleFiles.map((file: any, index: number) => {
          const text = String(file?.name ?? file?.label ?? file?.url ?? `文件 ${index + 1}`)
          const url = String(file?.url ?? '')
          return url
            ? h('a', {
                href: url,
                target: '_blank',
                rel: 'noopener noreferrer',
                class: ['text-primary underline-offset-4 hover:underline', props.linkClass ?? 'max-w-32'],
              }, text)
            : h('span', { class: props.textClass ?? 'max-w-32 truncate text-sm text-muted-foreground' }, text)
        }),
        remainCount > 0
          ? h(FaTag, {
              variant: props.moreVariant ?? 'secondary',
              class: 'h-5 rounded-full px-2 py-0 font-medium',
            }, () => `+${remainCount}`)
          : null,
      ])
    },
  })

  addRenderPlugin({
    name: 'actions',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const allActions = (props.actions ?? [])
        .filter((action: any) => (typeof action.visible === 'function' ? action.visible(context) : action.visible !== false))

      if (!allActions.length) {
        return props.emptyText ?? '-'
      }

      const mode = props.type ?? 'auto'
      const style = props.style ?? 'button'
      const fold = props.fold ?? 2
      const visibleActions = mode === 'dropdown'
        ? []
        : mode === 'tile'
          ? allActions
          : allActions.slice(0, fold)
      const moreActions = mode === 'tile'
        ? []
        : mode === 'dropdown'
          ? allActions
          : allActions.slice(fold)

      const renderButton = (action: any, index: number) => {
        const iconNode = action.icon
          ? h(FaIcon as any, {
              name: action.icon,
              class: action.label && style !== 'icon' ? 'mr-1 size-4' : 'size-4',
            })
          : null

        const buttonClass = style === 'icon'
          ? (action.class ?? props.iconButtonClass ?? 'rounded-xl')
          : (action.class ?? props.buttonClass ?? 'h-7 px-2 text-xs whitespace-nowrap shadow-none')

        const buttonNode = h(FaButton as any, {
          key: `${action.label ?? action.icon ?? 'action'}_${index}`,
          variant: action.variant ?? props.variant ?? 'outline',
          size: style === 'icon' ? (action.size ?? props.iconSize ?? 'icon-sm') : (action.size ?? props.size ?? 'sm'),
          disabled: typeof action.disabled === 'function' ? action.disabled(context) : action.disabled,
          class: buttonClass,
          onClick: () => action.handle?.(context),
        }, {
          default: () => style === 'icon'
            ? iconNode
            : [
                iconNode,
                action.label ?? null,
              ],
        })

        if (style !== 'icon') {
          return buttonNode
        }

        return h(FaTooltip as any, {
          text: action.label ?? props.iconTooltipText ?? '操作',
        }, {
          default: () => buttonNode,
        })
      }

      const wrapperClass = props.wrapperClass ?? (style === 'icon'
        ? 'flex flex-wrap items-center gap-2'
        : 'flex flex-nowrap items-center justify-center gap-1.5')
      const moreButtonClass = style === 'icon'
        ? (props.moreButtonClass ?? 'rounded-xl')
        : (props.moreButtonClass ?? 'h-7 min-w-7 px-2 text-xs whitespace-nowrap shadow-none')

      return h('div', { class: wrapperClass }, [
        ...visibleActions.map(renderButton),
        moreActions.length
          ? h(FaDropdown as any, {
              items: [[...moreActions.map((action: any) => ({
                label: action.label,
                icon: action.icon,
                variant: action.variant === 'destructive' ? 'destructive' : 'default',
                disabled: typeof action.disabled === 'function' ? action.disabled(context) : action.disabled,
                handle: () => action.handle?.(context),
              }))]],
            }, {
              default: () => h(FaButton as any, {
                variant: props.moreVariant ?? 'outline',
                size: style === 'icon' ? (props.iconSize ?? 'icon-sm') : (props.size ?? 'sm'),
                class: moreButtonClass,
              }, {
                default: () => style === 'icon'
                  ? h(FaIcon as any, { name: props.moreIcon ?? 'i-ri:more-2-line', class: 'size-4' })
                  : [
                      h(FaIcon as any, { name: props.moreIcon ?? 'i-ri:more-2-line', class: 'size-4' }),
                      props.moreText ?? null,
                    ],
              }),
            })
          : null,
      ])
    },
  })

  addRenderPlugin({
    name: 'ellipsis',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const text = String(context.value ?? '')
      if (!text) {
        return props.emptyText ?? '-'
      }

      const maxWidth = props.maxWidth ?? '240px'
      const content = h('span', {
        class: props.class ?? 'block truncate',
        style: {
          maxWidth,
        },
        title: props.nativeTitle === false ? undefined : text,
      }, text)

      if (props.tooltip === false) {
        return content
      }

      return h(FaTooltip as any, {
        text,
        side: props.side,
        align: props.align,
        delay: props.delay,
      }, {
        default: () => content,
      })
    },
  })

  addRenderPlugin({
    name: 'switch',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const modelValue = Boolean(context.value)
      return h(FaSwitch as any, {
        modelValue,
        'disabled': props.disabled ?? true,
        'onUpdate:modelValue': (nextValue: boolean) => {
          props.onChange?.(nextValue, context)
        },
      })
    },
  })

  addRenderPlugin({
    name: 'progress',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const percent = Number(context.value ?? 0)
      return h('div', { class: 'flex items-center gap-2 min-w-24' }, [
        h(FaProgress as any, {
          modelValue: Number.isFinite(percent) ? percent : 0,
          class: props.class ?? 'h-2 flex-1',
          ...props.progressProps,
        }),
        props.showText === false
          ? null
          : h('span', { class: 'text-xs text-muted-foreground tabular-nums' }, `${Math.max(0, Math.min(100, Math.round(percent)))}%`),
      ])
    },
  })

  addRenderPlugin({
    name: 'copyable',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const text = String(context.value ?? '')
      if (!text) {
        return props.emptyText ?? '-'
      }

      async function handleCopy() {
        try {
          await navigator.clipboard.writeText(text)
          props.onCopied?.(text, context)
          if (props.toast !== false) {
            useFaToast().success(props.successText ?? '复制成功')
          }
        }
        catch {
          if (props.toast !== false) {
            useFaToast().error(props.errorText ?? '复制失败')
          }
        }
      }

      return h('button', {
        type: 'button',
        class: 'inline-flex max-w-full items-center gap-1 rounded px-0 py-0 text-left text-primary hover:opacity-80',
        onClick: handleCopy,
      }, [
        h('span', {
          class: props.truncate === false ? '' : 'truncate max-w-48',
          title: text,
        }, text),
        h(FaIcon as any, {
          name: props.icon ?? 'i-ri:file-copy-line',
          class: 'size-3.5 shrink-0',
        }),
      ])
    },
  })

  addRenderPlugin({
    name: 'money',
    render: (context: ProTableRenderContext, props: Record<string, any> = {}) => {
      const amount = Number(context.value ?? 0)
      if (!Number.isFinite(amount)) {
        return props.emptyText ?? '-'
      }

      const content = formatMoney(amount, props)
      const colorClass = props.colored === false
        ? ''
        : amount > 0
          ? (props.positiveClass ?? 'text-emerald-600')
          : amount < 0
            ? (props.negativeClass ?? 'text-red-600')
            : (props.zeroClass ?? 'text-muted-foreground')

      return h('span', {
        class: ['tabular-nums font-medium', colorClass].filter(Boolean).join(' '),
      }, content)
    },
  })
}

export function useProTableRenderPlugin() {
  ensureBuiltins()

  return {
    addRenderPlugin,
    removeRenderPlugin,
    getRenderPlugins,
    getRenderPluginByName,
  }
}

export function useProTableToolbar() {
  ensureBuiltins()

  return {
    addToolbarPlugin,
    removeToolbarPlugin,
    getToolbarPlugin,
    getToolbarPlugins,
    hideToolbarPlugin,
    showToolbarPlugin,
  }
}
